import { useState } from "react";
import Decimal from "decimal.js";
import { calculateLastTradingDay } from "@/lib/commodities/dates";
import {
  getOptionMonthComboBase,
  deriveShipmentDatesFromShortDatedOptionMonth,
} from "@/lib/api/common";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import {
  OtcLayerAmendmentType,
  OtcRollDirection,
  Commodities,
  ContractTypeOptions,
  formulateOptionMonths,
  useCreateOtcContractAmendment,
} from "@/lib/api/otc";
import { Trader, Customer } from "@/lib/api/reports";
import {
  ContractFuturesStep,
  getStepsForContractType,
  computeCanolaStepFutures,
} from "@/section/otc/OtcBearState";
import {
  ContractRefGroup,
  isReownContractType,
  parseContractRefGroups,
  serializeContractRefGroups,
  validateContractRefGroups,
} from "../contract-form-v2/contract-reference-utils";
import { ReownContractReferenceEditor } from "../contract-form-v2/ReownContractReferenceEditor";
import {
  AmendmentSharedInput,
  ExpiredAmendmentInput,
  RollAmendmentInput,
  FuturesRollAmendmentInput,
  BasisRollAmendmentInput,
  OtcRollAmendmentInput,
  ContractLegsChangeFields,
  buildExitAmendmentRequest,
  buildExpiredAmendmentRequest,
  buildRollAmendmentRequest,
  buildFuturesRollAmendmentRequest,
  buildBasisRollAmendmentRequest,
  buildOtcRollAmendmentRequest,
  buildContractLegsChangeAmendmentRequest,
  buildSplitAmendmentRequest,
  rollHasAnyRealField,
  futuresRollHasAnyRealField,
  basisRollHasAnyRealField,
  otcRollHasAnyRealField,
} from "./amendmentPayloads";
import { parseLayerDate } from "./snapshotToForm";
import { isStaleLayerError } from "./layerErrors";
import { CONTRACT_TYPES_WITH_OPTION_MONTH_ETC_ONE } from "./layerFieldVisibility";
import {
  AMENDMENT_TYPE_OPTIONS,
  type AmendmentTypeChoice,
} from "./amendmentTypes";

interface OtcAmendmentWizardProps {
  open: boolean;
  contract_id: string;
  expected_current_layer_id: string;
  traders: Trader[];
  customers: Customer[];
  /** Current contract quantity, used for split remaining/child previews. */
  currentQuantity?: string;
  /**
   * Scalar snapshot of the current layer. Used to prefill roll / type-change
   * fields with the contract's current values and to gate the Canola futures
   * sub-section (commodity / contract_type).
   */
  currentSnapshot?: Record<string, unknown>;
  onClose: () => void;
  /**
   * Called after a successful amendment so the caller can refetch layers and
   * surface a type-specific confirmation (e.g. split created a new contract).
   */
  onAmended: (
    amendment_type: OtcLayerAmendmentType,
    amendment_language: string,
  ) => void;
  /**
   * Called when the backend rejects the amendment because the expected current
   * layer is stale. The caller should refetch the layer list so the user acts
   * on fresh state.
   */
  onStaleLayer: () => void;
}

function createEmptyShared(): AmendmentSharedInput {
  return {
    notes: undefined,
    has_invoice_fees: true,
    has_contract_adjustment: false,
    amendment_trader_id: undefined,
    adjustment: {
      option_premium: undefined,
      contract_fee: undefined,
      adjusted_date: new Date(),
    },
  };
}

/** Roll-specific fields the wizard collects (excludes shared/adjustment). */
interface RollFieldsState {
  rolled_option_month?: string;
  carry?: string;
  rolled_basis_month?: string;
  basis_carry?: string;
  shipment_start_date?: Date;
  shipment_end_date?: Date;
  end_date?: Date;
  crop_year?: string;
}

/**
 * Computes the expected expiration (last trading day) for a given option
 * month. Mirrors the helper used by the v2 OTC create form so the OTC Roll
 * amendment recomputes `end_date` consistently when option_month changes.
 */
function tryComputeExpiration(optionMonth: string | undefined): Date | undefined {
  if (!optionMonth) return undefined;
  try {
    const result = getOptionMonthComboBase(optionMonth);
    return calculateLastTradingDay(result.firstDayOfMonthMoment);
  } catch {
    return undefined;
  }
}

/**
 * Contract-type-change step fields the wizard collects. The backend clears
 * fields not used by the target type and validates the merged contract against
 * the target schema, so the user supplies explicit target step values here.
 */
interface TypeChangeFieldsState {
  step_one?: string;
  step_two?: string;
  step_three?: string;
  double_up?: string;
  option_month?: string;
  option_month_etc_one?: string;
}

const EMPTY_TYPE_CHANGE_FIELDS: TypeChangeFieldsState = {
  step_one: undefined,
  step_two: undefined,
  step_three: undefined,
  double_up: undefined,
  option_month: undefined,
  option_month_etc_one: undefined,
};

/**
 * Canola futures quote fields. Only relevant for Canola contracts whose
 * contract type has futures steps. The actual-futures inputs shown depend on
 * the contract type's steps (see `STEP_TO_CANOLA_FIELD`).
 */
interface CanolaFuturesState {
  implied_fx_amount?: string;
  actual_contract_futures?: string;
  actual_contract_futures_step_one?: string;
  actual_contract_futures_step_two?: string;
  actual_contract_futures_double_up?: string;
  final_futures_price?: string;
  final_futures_expected_price?: string;
  final_futures_price_date?: Date;
  /** Step values carried from the type-change fields for derived computation. */
  type_change_step_one?: string;
  type_change_step_two?: string;
  type_change_step_three?: string;
  type_change_double_up?: string;
}

const EMPTY_CANOLA: CanolaFuturesState = {
  implied_fx_amount: undefined,
  actual_contract_futures: undefined,
  actual_contract_futures_step_one: undefined,
  actual_contract_futures_step_two: undefined,
  actual_contract_futures_double_up: undefined,
  final_futures_price: undefined,
  final_futures_expected_price: undefined,
  final_futures_price_date: undefined,
  type_change_step_one: undefined,
  type_change_step_two: undefined,
  type_change_step_three: undefined,
  type_change_double_up: undefined,
};

/** Maps a futures step to its Canola "actual quoted futures" input field. */
const STEP_TO_CANOLA_FIELD: Record<
  ContractFuturesStep,
  { key: keyof CanolaFuturesState; label: string }
> = {
  step_one: { key: "actual_contract_futures_step_one", label: "Quoted Step 1 Futures" },
  step_two: { key: "actual_contract_futures_step_two", label: "Quoted Step 2 Futures" },
  step_three: { key: "actual_contract_futures", label: "Quoted Step 3 Futures" },
  double_up: { key: "actual_contract_futures_double_up", label: "Quoted Double Up Futures" },
};

/** Maps a futures step to the corresponding step value field in CanolaFuturesState. */
const STEP_FIELD_KEY: Record<ContractFuturesStep, keyof CanolaFuturesState> = {
  step_one: "type_change_step_one",
  step_two: "type_change_step_two",
  step_three: "type_change_step_three",
  double_up: "type_change_double_up",
};

/** Short labels for step column headers. */
const STEP_LABEL: Record<ContractFuturesStep, string> = {
  step_one: "Step 1",
  step_two: "Step 2",
  step_three: "Step 3",
  double_up: "Double Up",
};

/** Reads a non-empty scalar string from a snapshot value. */
function snapshotString(
  snapshot: Record<string, unknown> | undefined,
  key: string,
): string | undefined {
  if (!snapshot) return undefined;
  const value = snapshot[key];
  if (typeof value === "string") return value.trim() === "" ? undefined : value;
  if (typeof value === "number") return String(value);
  return undefined;
}

/** Roll fields prefilled from the current contract snapshot as a baseline. */
function prefillRoll(snapshot?: Record<string, unknown>): RollFieldsState {
  return {
    rolled_option_month:
      snapshotString(snapshot, "rolled_option_month") ??
      snapshotString(snapshot, "option_month"),
    carry: undefined,
    rolled_basis_month: snapshotString(snapshot, "rolled_basis_month"),
    basis_carry: undefined,
    shipment_start_date: parseLayerDate(snapshot?.shipment_start_date),
    shipment_end_date: parseLayerDate(snapshot?.shipment_end_date),
    end_date: parseLayerDate(snapshot?.end_date),
    crop_year: snapshotString(snapshot, "crop_year"),
  };
}

/** Type-change step fields prefilled from the current snapshot. */
function prefillTypeChange(snapshot?: Record<string, unknown>): TypeChangeFieldsState {
  return {
    step_one: snapshotString(snapshot, "step_one"),
    step_two: snapshotString(snapshot, "step_two"),
    step_three: snapshotString(snapshot, "step_three"),
    double_up: snapshotString(snapshot, "double_up"),
    option_month: snapshotString(snapshot, "option_month"),
    option_month_etc_one: snapshotString(snapshot, "option_month_etc_one"),
  };
}

/**
 * Canola fields prefilled from the current snapshot. All Canola-specific
 * contract values are seeded so the user can see and reference existing
 * values when filling in an amendment.
 */
function prefillCanola(snapshot?: Record<string, unknown>): CanolaFuturesState {
  return {
    ...EMPTY_CANOLA,
    implied_fx_amount: snapshotString(snapshot, "implied_fx_amount"),
    actual_contract_futures: snapshotString(snapshot, "actual_contract_futures"),
    actual_contract_futures_step_one: snapshotString(snapshot, "actual_contract_futures_step_one"),
    actual_contract_futures_step_two: snapshotString(snapshot, "actual_contract_futures_step_two"),
    actual_contract_futures_double_up: snapshotString(snapshot, "actual_contract_futures_double_up"),
    final_futures_price: snapshotString(snapshot, "final_futures_price"),
    final_futures_expected_price: snapshotString(snapshot, "final_futures_expected_price"),
    final_futures_price_date: parseLayerDate(snapshot?.final_futures_price_date),
    type_change_step_one: snapshotString(snapshot, "step_one"),
    type_change_step_two: snapshotString(snapshot, "step_two"),
    type_change_step_three: snapshotString(snapshot, "step_three"),
    type_change_double_up: snapshotString(snapshot, "double_up"),
  };
}

/** Canola futures inputs, shown for Canola contracts with futures steps. */
function CanolaFuturesSection({
  steps,
  state,
  onChange,
  quantity,
}: {
  steps: ContractFuturesStep[];
  state: CanolaFuturesState;
  onChange: (
    key: keyof CanolaFuturesState,
    value: string | Date | undefined,
  ) => void;
  /** Current contract quantity, used to compute futures margin. */
  quantity?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-semibold uppercase text-muted-foreground">
        Canola Futures
      </span>
      <div className="grid grid-cols-2 gap-3">
        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Implied FX Amount</Label>
          <Input
            type="text"
            value={state.implied_fx_amount ?? ""}
            onChange={(e) =>
              onChange("implied_fx_amount", e.target.value || undefined)
            }
          />
        </div>
      </div>
      {steps.map((step: ContractFuturesStep) => {
        const meta = STEP_TO_CANOLA_FIELD[step];
        const stepField = STEP_FIELD_KEY[step];
        const stepValue = state[stepField];
        const actualValue = state[meta.key];
        const derived = computeCanolaStepFutures({
          stepValue: (stepValue as string | undefined) ?? 0,
          implied_fx_amount: state.implied_fx_amount ?? 0,
          actualContractFutures: (actualValue as string | undefined) ?? 0,
          quantity: quantity ?? 0,
        });
        return (
          <div key={step} className="grid grid-cols-4 gap-3">
            <div className="grid items-center gap-1.5">
              <Label className="font-semibold text-muted-foreground">
                Calculated {STEP_LABEL[step]}
              </Label>
              <Input
                type="text"
                disabled
                readOnly
                value={derived.expected_contract_futures.toFixed(4)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 disabled:cursor-default disabled:opacity-70"
              />
            </div>
            <div className="grid items-center gap-1.5">
              <Label className="font-semibold">{meta.label}</Label>
              <Input
                type="text"
                value={typeof actualValue === "string" ? actualValue : ""}
                onChange={(e) => onChange(meta.key, e.target.value || undefined)}
              />
            </div>
            <div className="grid items-center gap-1.5">
              <Label className="font-semibold text-muted-foreground">
                {STEP_LABEL[step]} Margin
              </Label>
              <Input
                type="text"
                disabled
                readOnly
                value={derived.futures_margin.toFixed(4)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 disabled:cursor-default disabled:opacity-70"
              />
            </div>
          </div>
        );
      })}
      <div className="grid grid-cols-3 gap-3">
        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Final Quoted</Label>
          <Input
            type="text"
            value={state.final_futures_price ?? ""}
            onChange={(e) =>
              onChange("final_futures_price", e.target.value || undefined)
            }
          />
        </div>
        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Final Priced Amount</Label>
          <Input
            type="text"
            value={state.final_futures_expected_price ?? ""}
            onChange={(e) =>
              onChange(
                "final_futures_expected_price",
                e.target.value || undefined,
              )
            }
          />
        </div>
        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Final Price Date</Label>
          <DatePicker
            value={state.final_futures_price_date}
            onChange={(date: Date | undefined) =>
              onChange("final_futures_price_date", date)
            }
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Wizard for creating a new amendment layer.
 *
 * Supports Exit, Roll (up/down), Contract Type Change, and Split. Roll/basis
 * months use option-month comboboxes; Canola contracts with futures steps
 * surface a Canola futures sub-section on roll and contract-type-change. Roll
 * and type-change fields prefill from the current contract snapshot.
 */
export function OtcAmendmentWizard({
  open,
  contract_id,
  expected_current_layer_id,
  traders,
  customers,
  currentQuantity,
  currentSnapshot,
  onClose,
  onAmended,
  onStaleLayer,
}: OtcAmendmentWizardProps) {
  const createAmendment = useCreateOtcContractAmendment();

  const currentCommodity = snapshotString(currentSnapshot, "commodity");
  const currentContractType = snapshotString(currentSnapshot, "implied_pairing_contract_type") || snapshotString(currentSnapshot, "contract_type");
  const isCanola = currentCommodity === Commodities.Canola;
  const isReown = isReownContractType(currentContractType);

  const [choice, setChoice] = useState<AmendmentTypeChoice>("exit");
  const [shared, setShared] = useState<AmendmentSharedInput>(createEmptyShared());
  const [exitDate, setExitDate] = useState<Date | undefined>(undefined);
  const [exitQuantity, setExitQuantity] = useState<string>("");
  const [exitFinalFuturesPrice, setExitFinalFuturesPrice] = useState<string>("");
  const [expirationDate, setExpirationDate] = useState<Date | undefined>(undefined);
  const [roll, setRoll] = useState<RollFieldsState>(() =>
    prefillRoll(currentSnapshot),
  );
  const [typeChangeFields, setTypeChangeFields] = useState<TypeChangeFieldsState>(
    () => prefillTypeChange(currentSnapshot),
  );
  // Effective pairing type for a legs-change amendment. Prefilled with the
  // contract's current type; the steps the user may edit are derived from it.
  const [legsChangeImpliedType, setLegsChangeImpliedType] = useState<string>(
    () => currentContractType ?? "",
  );
  const [canola, setCanola] = useState<CanolaFuturesState>(() =>
    prefillCanola(currentSnapshot),
  );
  const [splitCustomerId, setSplitCustomerId] = useState<string>("");
  const [splitQuantity, setSplitQuantity] = useState<string>("");
  const [splitRefGroups, setSplitRefGroups] = useState<ContractRefGroup[]>(() =>
    parseContractRefGroups(undefined),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feeAdjustmentTouched, setFeeAdjustmentTouched] = useState(false);

  const splitRefErrors = isReown ? validateContractRefGroups(splitRefGroups, { requireFirstGroup: false }) : null;

  const reset = () => {
    setChoice("exit");
    setShared(createEmptyShared());
    setExitDate(undefined);
    setExitQuantity("");
    setExitFinalFuturesPrice("");
    setExpirationDate(undefined);
    setRoll(prefillRoll(currentSnapshot));
    setTypeChangeFields(prefillTypeChange(currentSnapshot));
    setLegsChangeImpliedType(currentContractType ?? "");
    setCanola(prefillCanola(currentSnapshot));
    setSplitCustomerId("");
    setSplitQuantity("");
    setSplitRefGroups(parseContractRefGroups(undefined));
    setFeeAdjustmentTouched(false);
    setError(null);
  };

  const handleClose = () => {
    if (isSaving) return;
    reset();
    onClose();
  };

  const setAdjustment = (
    key: keyof AmendmentSharedInput["adjustment"],
    value: string | Date | undefined,
  ) => {
    setShared((prev: AmendmentSharedInput) => ({
      ...prev,
      adjustment: { ...prev.adjustment, [key]: value },
    }));
  };

  const isRoll = choice === "roll_up" || choice === "roll_down";
  const isFuturesRoll = choice === "futures_roll";
  const isBasisRoll = choice === "basis_roll";
  const isOtcRoll = choice === "otc_roll";
  const isLegsChange = choice === "contract_legs_change";
  const isSplit = choice === "split";
  const isExpired = choice === "expired";
  const isSupported = choice === "exit" || isRoll || isFuturesRoll || isBasisRoll || isOtcRoll || isLegsChange || isSplit || isExpired;

  // Canola futures steps for the relevant contract type: always the current
  // type, since neither a roll nor a legs change alters the contract type.
  const isRollLike = isRoll || isFuturesRoll || isBasisRoll || isOtcRoll;
  const rollCanolaSteps =
    isRollLike && isCanola ? getStepsForContractType(currentContractType) : [];
  // Legs-change leg universe is derived from the effective pairing type
  // (`implied_pairing_contract_type`), not the contract's actual type, so the
  // user may add/edit legs for the paired strategy (e.g. min_max -> range_w_du).
  const legsChangeCanolaSteps =
    isLegsChange && isCanola ? getStepsForContractType(legsChangeImpliedType) : [];

  // Legs-change step visibility: only show steps relevant to the implied type.
  const legsChangeStepKeys: ContractFuturesStep[] = isLegsChange
    ? getStepsForContractType(legsChangeImpliedType)
    : [];
  const showLegsChangeStep = (step: ContractFuturesStep): boolean =>
    legsChangeStepKeys.includes(step);

  // OTC roll step visibility: show steps relevant to the current contract type.
  const otcRollStepKeys: ContractFuturesStep[] = isOtcRoll
    ? getStepsForContractType(currentContractType)
    : [];
  const showOtcRollStep = (step: ContractFuturesStep): boolean =>
    otcRollStepKeys.includes(step);

  /** Merges entered Canola fields into a builder input shape. */
  const canolaForBuilder = (): Partial<CanolaFuturesState> => ({
    implied_fx_amount: canola.implied_fx_amount,
    actual_contract_futures: canola.actual_contract_futures,
    actual_contract_futures_step_one: canola.actual_contract_futures_step_one,
    actual_contract_futures_step_two: canola.actual_contract_futures_step_two,
    actual_contract_futures_double_up: canola.actual_contract_futures_double_up,
    final_futures_price: canola.final_futures_price,
    final_futures_expected_price: canola.final_futures_expected_price,
    final_futures_price_date: canola.final_futures_price_date,
  });

  const buildRollInput = (): RollAmendmentInput => ({
    ...shared,
    roll_direction: choice === "roll_down" ? "down" : "up",
    rolled_option_month: roll.rolled_option_month,
    carry: roll.carry,
    rolled_basis_month: roll.rolled_basis_month,
    basis_carry: roll.basis_carry,
    shipment_start_date: roll.shipment_start_date,
    shipment_end_date: roll.shipment_end_date,
    crop_year:
      roll.crop_year && roll.crop_year.trim() !== ""
        ? parseInt(roll.crop_year, 10)
        : undefined,
    ...(isCanola ? canolaForBuilder() : {}),
  });

  const buildFuturesRollInput = (): FuturesRollAmendmentInput => ({
    ...shared,
    rolled_option_month: roll.rolled_option_month,
    carry: roll.carry,
    shipment_start_date: roll.shipment_start_date,
    shipment_end_date: roll.shipment_end_date,
    crop_year:
      roll.crop_year && roll.crop_year.trim() !== ""
        ? parseInt(roll.crop_year, 10)
        : undefined,
    ...(isCanola ? canolaForBuilder() : {}),
  });

  const buildBasisRollInput = (): BasisRollAmendmentInput => ({
    ...shared,
    rolled_basis_month: roll.rolled_basis_month,
    basis_carry: roll.basis_carry,
    shipment_start_date: roll.shipment_start_date,
    shipment_end_date: roll.shipment_end_date,
    crop_year:
      roll.crop_year && roll.crop_year.trim() !== ""
        ? parseInt(roll.crop_year, 10)
        : undefined,
    ...(isCanola ? canolaForBuilder() : {}),
  });

  const buildOtcRollInput = (): OtcRollAmendmentInput => {
    // Only include steps relevant to the current contract type.
    const relevantSteps = new Set(otcRollStepKeys);
    return {
      ...shared,
      option_month: typeChangeFields.option_month,
      option_month_etc_one: typeChangeFields.option_month_etc_one,
      step_one: relevantSteps.has("step_one") ? typeChangeFields.step_one : undefined,
      step_two: relevantSteps.has("step_two") ? typeChangeFields.step_two : undefined,
      step_three: relevantSteps.has("step_three") ? typeChangeFields.step_three : undefined,
      double_up: relevantSteps.has("double_up") ? typeChangeFields.double_up : undefined,
      shipment_start_date: roll.shipment_start_date,
      shipment_end_date: roll.shipment_end_date,
      end_date: roll.end_date,
      crop_year:
        roll.crop_year && roll.crop_year.trim() !== ""
          ? parseInt(roll.crop_year, 10)
          : undefined,
      ...(isCanola ? canolaForBuilder() : {}),
    };
  };

  // Exit quantity preview (Decimal; never JS number math). Shows the contract
  // quantity remaining after the exited quantity is removed.
  const exitPreview = (() => {
    if (choice !== "exit") return null;
    const sourceRaw = currentQuantity;
    const exitRaw = exitQuantity;
    if (!sourceRaw || !exitRaw || exitRaw.trim() === "") return null;
    try {
      const source = new Decimal(sourceRaw);
      const exit = new Decimal(exitRaw);
      if (exit.lte(0) || exit.gt(source)) return { source, exit, remaining: null };
      return { source, exit, remaining: source.sub(exit) };
    } catch {
      return null;
    }
  })();

  // Split quantity previews (Decimal; never JS number math).
  const splitPreview = (() => {
    if (!isSplit) return null;
    const sourceRaw = currentQuantity;
    const splitRaw = splitQuantity;
    if (!sourceRaw || !splitRaw || splitRaw.trim() === "") return null;
    try {
      const source = new Decimal(sourceRaw);
      const split = new Decimal(splitRaw);
      if (split.lte(0) || split.gte(source)) return { source, split, remaining: null };
      return { source, split, remaining: source.sub(split) };
    } catch {
      return null;
    }
  })();

  const handleSubmit = async () => {
    if (isSaving) return;
    setError(null);
    setFeeAdjustmentTouched(false);

    if (!isSupported) {
      setError("This amendment type is not available yet.");
      return;
    }

    if (!isExpired && !shared.has_invoice_fees && !shared.has_contract_adjustment) {
      setFeeAdjustmentTouched(true);
      setError("Select either Invoice Fees or Contract Fee Add/Ded.");
      return;
    }

    let request;
    if (isExpired) {
      if (!shared.amendment_trader_id) {
        setError("Select the amendment trader.");
        return;
      }
      if (!shared.notes || shared.notes.trim() === "") {
        setError("Enter notes for the expired amendment.");
        return;
      }
      const expiredInput: ExpiredAmendmentInput = {
        amendment_trader_id: shared.amendment_trader_id,
        notes: shared.notes.trim(),
        expiration_date: expirationDate,
      };
      request = buildExpiredAmendmentRequest(
        expected_current_layer_id,
        expiredInput,
      );
    } else if (choice === "exit") {
      if (!exitDate) {
        setError("Enter the exit date.");
        return;
      }
      if (exitQuantity.trim() === "") {
        setError("Enter the exit quantity.");
        return;
      }
      // NOTE: Exit final futures priced is not required
      // That is what differentiates an exit between a priced and non priced exit

      if (!exitPreview || exitPreview.remaining === null) {
        setError(
          "Exit quantity must be greater than 0 and not exceed the contract quantity.",
        );
        return;
      }
      request = buildExitAmendmentRequest(expected_current_layer_id, {
        ...shared,
        exit_date: exitDate,
        exit_quantity: exitQuantity,
        exit_final_futures_price: exitFinalFuturesPrice,
      });
    } else if (isRoll) {
      const rollInput = buildRollInput();
      if (!rollHasAnyRealField(rollInput)) {
        setError(
          "A roll amendment requires at least one roll field (month, carry, shipment date, or crop year).",
        );
        return;
      }
      request = buildRollAmendmentRequest(expected_current_layer_id, rollInput);
    } else if (isFuturesRoll) {
      const futuresRollInput = buildFuturesRollInput();
      if (!futuresRollHasAnyRealField(futuresRollInput)) {
        setError(
          "A futures roll amendment requires at least one field (month, carry, shipment date, or crop year).",
        );
        return;
      }
      request = buildFuturesRollAmendmentRequest(expected_current_layer_id, futuresRollInput);
    } else if (isBasisRoll) {
      const basisRollInput = buildBasisRollInput();
      if (!basisRollHasAnyRealField(basisRollInput)) {
        setError(
          "A basis roll amendment requires at least one field (month, carry, shipment date, or crop year).",
        );
        return;
      }
      request = buildBasisRollAmendmentRequest(expected_current_layer_id, basisRollInput);
    } else if (isOtcRoll) {
      const otcRollInput = buildOtcRollInput();
      if (!otcRollHasAnyRealField(otcRollInput)) {
        setError(
          "An OTC roll amendment requires at least one field (option month, step, shipment date, or crop year).",
        );
        return;
      }
      // If the OTC opt. month changed from the current snapshot value, an
      // Expiration Date must be supplied (matches the v2 create form rule).
      const snapshotOptionMonth = snapshotString(currentSnapshot, "option_month");
      const optionMonthChanged =
        (otcRollInput.option_month ?? undefined) !== (snapshotOptionMonth ?? undefined);
      if (optionMonthChanged && !otcRollInput.end_date) {
        setError(
          "An Expiration Date is required when changing the OTC Opt. Month on an OTC roll amendment.",
        );
        return;
      }
      request = buildOtcRollAmendmentRequest(expected_current_layer_id, otcRollInput);
    } else if (isLegsChange) {
      if (!legsChangeImpliedType) {
        setError("Select the implied pairing contract type.");
        return;
      }
      // Resolve a text field to the delta the backend expects:
      //   non-empty value        -> set (string)
      //   emptied a prefilled val -> clear (null)
      //   untouched empty         -> omit (undefined)
      const resolveText = (
        current: string | undefined,
        baseline: string | undefined,
      ): string | null | undefined => {
        const trimmed = current?.trim() ?? "";
        if (trimmed !== "") return trimmed;
        return baseline !== undefined ? null : undefined;
      };
      const resolveDate = (
        current: Date | undefined,
        baseline: Date | undefined,
      ): Date | null | undefined => {
        if (current) return current;
        return baseline !== undefined ? null : undefined;
      };

      const relevantSteps = new Set(legsChangeStepKeys);
      const fields: ContractLegsChangeFields = {};
      if (relevantSteps.has("step_one")) {
        fields.step_one = resolveText(
          typeChangeFields.step_one,
          snapshotString(currentSnapshot, "step_one"),
        );
      }
      if (relevantSteps.has("step_two")) {
        fields.step_two = resolveText(
          typeChangeFields.step_two,
          snapshotString(currentSnapshot, "step_two"),
        );
      }
      if (relevantSteps.has("step_three")) {
        fields.step_three = resolveText(
          typeChangeFields.step_three,
          snapshotString(currentSnapshot, "step_three"),
        );
      }
      if (relevantSteps.has("double_up")) {
        fields.double_up = resolveText(
          typeChangeFields.double_up,
          snapshotString(currentSnapshot, "double_up"),
        );
      }

      // Any step populated on the current contract but not legal for the newly
      // selected implied type must be explicitly cleared; the backend rejects a
      // merged state containing a leg outside the effective pairing type.
      const ALL_STEPS: ContractFuturesStep[] = [
        "step_one",
        "step_two",
        "step_three",
        "double_up",
      ];
      for (const step of ALL_STEPS) {
        if (relevantSteps.has(step)) continue;
        if (snapshotString(currentSnapshot, step) !== undefined) {
          fields[step] = null;
        }
      }

      if (isCanola && legsChangeCanolaSteps.length > 0) {
        fields.implied_fx_amount = resolveText(
          canola.implied_fx_amount,
          snapshotString(currentSnapshot, "implied_fx_amount"),
        );
        fields.actual_contract_futures = resolveText(
          canola.actual_contract_futures,
          snapshotString(currentSnapshot, "actual_contract_futures"),
        );
        fields.actual_contract_futures_step_one = resolveText(
          canola.actual_contract_futures_step_one,
          snapshotString(currentSnapshot, "actual_contract_futures_step_one"),
        );
        fields.actual_contract_futures_step_two = resolveText(
          canola.actual_contract_futures_step_two,
          snapshotString(currentSnapshot, "actual_contract_futures_step_two"),
        );
        fields.actual_contract_futures_double_up = resolveText(
          canola.actual_contract_futures_double_up,
          snapshotString(currentSnapshot, "actual_contract_futures_double_up"),
        );
        fields.final_futures_price = resolveText(
          canola.final_futures_price,
          snapshotString(currentSnapshot, "final_futures_price"),
        );
        fields.final_futures_expected_price = resolveText(
          canola.final_futures_expected_price,
          snapshotString(currentSnapshot, "final_futures_expected_price"),
        );
        fields.final_futures_price_date = resolveDate(
          canola.final_futures_price_date,
          parseLayerDate(currentSnapshot?.final_futures_price_date),
        );
      }

      const hasAnyField = Object.values(fields).some(
        (value) => value !== undefined,
      );
      if (!hasAnyField) {
        setError(
          "A contract legs change requires at least one field to set or clear.",
        );
        return;
      }

      request = buildContractLegsChangeAmendmentRequest(
        expected_current_layer_id,
        {
          ...shared,
          implied_pairing_contract_type: legsChangeImpliedType,
          fields,
        },
      );
    } else {
      if (!splitCustomerId) {
        setError("Select the customer for the split contract.");
        return;
      }
      if (!splitQuantity || splitQuantity.trim() === "") {
        setError("Enter the split quantity.");
        return;
      }
      if (isReown && splitRefErrors !== null) {
        setError("Please complete the contract references.");
        return;
      }
      const serializedRef = isReown
        ? serializeContractRefGroups(splitRefGroups) || undefined
        : undefined;
      request = buildSplitAmendmentRequest(expected_current_layer_id, {
        ...shared,
        split_customer_id: splitCustomerId,
        split_quantity: splitQuantity,
        contract_reference: serializedRef,
      });
    }

    const amendmentType = request.amendment_type;

    setIsSaving(true);
    try {
      const result = await createAmendment(contract_id, request);
      if (!result.success) {
        if (isStaleLayerError(result.message)) {
          // The current layer moved on; refetch and close so the user retries
          // against fresh state instead of a stale layer id.
          reset();
          onClose();
          onStaleLayer();
          return;
        }
        setError(result.message || "Failed to create amendment.");
        return;
      }
      reset();
      onClose();
      onAmended(amendmentType, result.data.amendment_language);
    } finally {
      setIsSaving(false);
    }
  };

  const setRollField = (
    key: keyof RollFieldsState,
    value: string | Date | undefined,
  ) => {
    setRoll((prev: RollFieldsState) => ({ ...prev, [key]: value }));
  };

  const STEP_TO_CANOLA_TYPE_CHANGE: Record<string, keyof CanolaFuturesState> = {
    step_one: "type_change_step_one",
    step_two: "type_change_step_two",
    step_three: "type_change_step_three",
    double_up: "type_change_double_up",
  };

  const setTypeChangeField = (
    key: keyof TypeChangeFieldsState,
    value: string | undefined,
  ) => {
    setTypeChangeFields((prev: TypeChangeFieldsState) => ({
      ...prev,
      [key]: value,
    }));
    // Sync step values into canola state so derived futures update live.
    const canolaKey = STEP_TO_CANOLA_TYPE_CHANGE[key];
    if (canolaKey) {
      setCanola((prev: CanolaFuturesState) => ({ ...prev, [canolaKey]: value }));
    }
  };

  const setCanolaField = (
    key: keyof CanolaFuturesState,
    value: string | Date | undefined,
  ) => {
    setCanola((prev: CanolaFuturesState) => ({ ...prev, [key]: value }));
  };

  const traderOptions = traders.map((t: Trader) => ({
    value: t.id,
    label: t.name || "",
  })).sort((a,b) => a.label.localeCompare(b.label));
  const customerOptions = customers.map((c: Customer) => ({
    value: c.id,
    label: c.long_name,
  })).sort((a,b) => a.label.localeCompare(b.label));
  const rolledOptionMonthOptions = formulateOptionMonths(roll.rolled_option_month);
  const rolledBasisMonthOptions = formulateOptionMonths(roll.rolled_basis_month);
  const otcOptionMonthOptions = formulateOptionMonths(typeChangeFields.option_month);
  const otcOptionMonthEtcOneOptions = formulateOptionMonths(
    typeChangeFields.option_month_etc_one,
  );

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => (next ? undefined : handleClose())}>
      <DialogContent className="min-w-[40vw] max-w-[80vw] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Add Amendment</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4  overflow-y-auto pr-1">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to create amendment</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid items-center gap-1.5">
            <Label className="font-semibold">Amendment Type</Label>
            <Combobox
              dwimOnChange
              isModal
              value={choice}
              onValueChange={(value: string) =>
                setChoice(value as AmendmentTypeChoice)
              }
              options={AMENDMENT_TYPE_OPTIONS}
            />
          </div>

          {choice === "exit" && (
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-3 gap-3">
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Exit Date</Label>
                  <DatePicker value={exitDate} onChange={setExitDate} />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Exit Quantity</Label>
                  <Input
                    type="text"
                    value={exitQuantity}
                    onChange={(e) => setExitQuantity(e.target.value)}
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Exit Final Futures Price</Label>
                  <Input
                    type="text"
                    value={exitFinalFuturesPrice}
                    onChange={(e) => setExitFinalFuturesPrice(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid items-center gap-1.5">
                <Label className="font-semibold text-muted-foreground">
                  Resulting Contract Quantity
                </Label>
                <Input
                  type="text"
                  disabled
                  readOnly
                  value={
                    exitPreview && exitPreview.remaining !== null
                      ? exitPreview.remaining.toString()
                      : ""
                  }
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 disabled:cursor-default disabled:opacity-70"
                />
                {exitQuantity.trim() !== "" &&
                  (!exitPreview || exitPreview.remaining === null) && (
                    <span className="text-xs text-orange-500">
                      Exit quantity must be greater than 0 and not exceed the
                      contract quantity ({currentQuantity ?? "n/a"}).
                    </span>
                  )}
              </div>
            </div>
          )}

          {isExpired && (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-muted-foreground">
                Quantity being expired: {currentQuantity ?? "n/a"}
              </span>
            </div>
          )}

          {isRoll && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Roll {choice === "roll_down" ? "Down" : "Up"} Fields
              </span>
              <div className="grid grid-cols-4 gap-3">
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Futures Rolled Month</Label>
                  <Combobox
                    dwimOnChange
                    isModal
                    value={roll.rolled_option_month ?? ""}
                    onValueChange={(value: string) =>
                      setRollField("rolled_option_month", value || undefined)
                    }
                    options={rolledOptionMonthOptions}
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Futures Carry</Label>
                  <Input
                    type="text"
                    value={roll.carry ?? ""}
                    onChange={(e) =>
                      setRollField("carry", e.target.value || undefined)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Basis Rolled Month</Label>
                  <Combobox
                    isModal
                    dwimOnChange
                    value={roll.rolled_basis_month ?? ""}
                    onValueChange={(value: string) =>
                      setRollField("rolled_basis_month", value || undefined)
                    }
                    options={rolledBasisMonthOptions}
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Basis Carry</Label>
                  <Input
                    type="text"
                    value={roll.basis_carry ?? ""}
                    onChange={(e) =>
                      setRollField("basis_carry", e.target.value || undefined)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Shipment Start Date</Label>
                  <DatePicker
                    value={roll.shipment_start_date}
                    onChange={(date: Date | undefined) =>
                      setRollField("shipment_start_date", date)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Shipment End Date</Label>
                  <DatePicker
                    value={roll.shipment_end_date}
                    onChange={(date: Date | undefined) =>
                      setRollField("shipment_end_date", date)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Crop Year</Label>
                  <Input
                    type="number"
                    value={roll.crop_year ?? ""}
                    onChange={(e) =>
                      setRollField("crop_year", e.target.value || undefined)
                    }
                  />
                </div>
              </div>
              {rollCanolaSteps.length > 0 && (
                <CanolaFuturesSection
                  steps={rollCanolaSteps}
                  state={canola}
                  onChange={setCanolaField}
                  quantity={currentQuantity}
                />
              )}
            </div>
          )}

          {isFuturesRoll && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Futures Roll Fields
              </span>
              <div className="grid grid-cols-4 gap-3">
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Futures Rolled Month</Label>
                  <Combobox
                    dwimOnChange
                    isModal
                    value={roll.rolled_option_month ?? ""}
                    onValueChange={(value: string) =>
                      setRollField("rolled_option_month", value || undefined)
                    }
                    options={rolledOptionMonthOptions}
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Futures Carry</Label>
                  <Input
                    type="text"
                    value={roll.carry ?? ""}
                    onChange={(e) =>
                      setRollField("carry", e.target.value || undefined)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Shipment Start Date</Label>
                  <DatePicker
                    value={roll.shipment_start_date}
                    onChange={(date: Date | undefined) =>
                      setRollField("shipment_start_date", date)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Shipment End Date</Label>
                  <DatePicker
                    value={roll.shipment_end_date}
                    onChange={(date: Date | undefined) =>
                      setRollField("shipment_end_date", date)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Crop Year</Label>
                  <Input
                    type="number"
                    value={roll.crop_year ?? ""}
                    onChange={(e) =>
                      setRollField("crop_year", e.target.value || undefined)
                    }
                  />
                </div>
              </div>
              {rollCanolaSteps.length > 0 && (
                <CanolaFuturesSection
                  steps={rollCanolaSteps}
                  state={canola}
                  onChange={setCanolaField}
                  quantity={currentQuantity}
                />
              )}
            </div>
          )}

          {isBasisRoll && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Basis Roll Fields
              </span>
              <div className="grid grid-cols-4 gap-3">
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Basis Rolled Month</Label>
                  <Combobox
                    isModal
                    dwimOnChange
                    value={roll.rolled_basis_month ?? ""}
                    onValueChange={(value: string) =>
                      setRollField("rolled_basis_month", value || undefined)
                    }
                    options={rolledBasisMonthOptions}
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Basis Carry</Label>
                  <Input
                    type="text"
                    value={roll.basis_carry ?? ""}
                    onChange={(e) =>
                      setRollField("basis_carry", e.target.value || undefined)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Shipment Start Date</Label>
                  <DatePicker
                    value={roll.shipment_start_date}
                    onChange={(date: Date | undefined) =>
                      setRollField("shipment_start_date", date)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Shipment End Date</Label>
                  <DatePicker
                    value={roll.shipment_end_date}
                    onChange={(date: Date | undefined) =>
                      setRollField("shipment_end_date", date)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Crop Year</Label>
                  <Input
                    type="number"
                    value={roll.crop_year ?? ""}
                    onChange={(e) =>
                      setRollField("crop_year", e.target.value || undefined)
                    }
                  />
                </div>
              </div>
              {rollCanolaSteps.length > 0 && (
                <CanolaFuturesSection
                  steps={rollCanolaSteps}
                  state={canola}
                  onChange={setCanolaField}
                  quantity={currentQuantity}
                />
              )}
            </div>
          )}

          {isOtcRoll && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                OTC Roll Fields
              </span>
              <div className="grid grid-cols-4 gap-3">
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">OTC Opt. Month</Label>
                  <Combobox
                    dwimOnChange
                    isModal
                    value={typeChangeFields.option_month ?? ""}
                    onValueChange={(value: string) => {
                      const next = value || undefined;
                      setTypeChangeField("option_month", next);
                      const exp = tryComputeExpiration(next);
                      // Short-dated option months derive shipment dates from
                      // the inherited contract commodity. When no dates can be
                      // derived (non short-dated month, or no match) leave the
                      // user's existing shipment dates untouched. Batch all
                      // roll-field updates into a single state change so they
                      // do not stomp each other.
                      const shipment =
                        deriveShipmentDatesFromShortDatedOptionMonth(
                          currentCommodity,
                          next,
                        );
                      setRoll((prev: RollFieldsState) => ({
                        ...prev,
                        ...(exp ? { end_date: exp } : {}),
                        ...(shipment
                          ? {
                              shipment_start_date: shipment.shipment_start_date,
                              shipment_end_date: shipment.shipment_end_date,
                            }
                          : {}),
                      }));
                    }}
                    options={otcOptionMonthOptions}
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Expiration Date</Label>
                  <DatePicker
                    value={roll.end_date}
                    onChange={(date: Date | undefined) =>
                      setRollField("end_date", date)
                    }
                  />
                </div>
                {!!currentContractType &&
                  CONTRACT_TYPES_WITH_OPTION_MONTH_ETC_ONE.has(currentContractType) && (
                    <div className="grid items-center gap-1.5">
                      <Label className="font-semibold">HTA DU Opt. Month</Label>
                      <Combobox
                        dwimOnChange
                        isModal
                        value={typeChangeFields.option_month_etc_one ?? ""}
                        onValueChange={(value: string) =>
                          setTypeChangeField("option_month_etc_one", value || undefined)
                        }
                        options={otcOptionMonthEtcOneOptions}
                      />
                    </div>
                  )}
                {showOtcRollStep("step_one") && (
                  <div className="grid items-center gap-1.5">
                    <Label className="font-semibold">Step 1</Label>
                    <Input
                      type="text"
                      value={typeChangeFields.step_one ?? ""}
                      onChange={(e) =>
                        setTypeChangeField("step_one", e.target.value || undefined)
                      }
                    />
                  </div>
                )}
                {showOtcRollStep("step_two") && (
                  <div className="grid items-center gap-1.5">
                    <Label className="font-semibold">Step 2</Label>
                    <Input
                      type="text"
                      value={typeChangeFields.step_two ?? ""}
                      onChange={(e) =>
                        setTypeChangeField("step_two", e.target.value || undefined)
                      }
                    />
                  </div>
                )}
                {showOtcRollStep("step_three") && (
                  <div className="grid items-center gap-1.5">
                    <Label className="font-semibold">Step 3</Label>
                    <Input
                      type="text"
                      value={typeChangeFields.step_three ?? ""}
                      onChange={(e) =>
                        setTypeChangeField("step_three", e.target.value || undefined)
                      }
                    />
                  </div>
                )}
                {showOtcRollStep("double_up") && (
                  <div className="grid items-center gap-1.5">
                    <Label className="font-semibold">Double Up</Label>
                    <Input
                      type="text"
                      value={typeChangeFields.double_up ?? ""}
                      onChange={(e) =>
                        setTypeChangeField("double_up", e.target.value || undefined)
                      }
                    />
                  </div>
                )}
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Shipment Start Date</Label>
                  <DatePicker
                    value={roll.shipment_start_date}
                    onChange={(date: Date | undefined) =>
                      setRollField("shipment_start_date", date)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Shipment End Date</Label>
                  <DatePicker
                    value={roll.shipment_end_date}
                    onChange={(date: Date | undefined) =>
                      setRollField("shipment_end_date", date)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Crop Year</Label>
                  <Input
                    type="number"
                    value={roll.crop_year ?? ""}
                    onChange={(e) =>
                      setRollField("crop_year", e.target.value || undefined)
                    }
                  />
                </div>
              </div>
              {rollCanolaSteps.length > 0 && (
                <CanolaFuturesSection
                  steps={rollCanolaSteps}
                  state={canola}
                  onChange={setCanolaField}
                  quantity={currentQuantity}
                />
              )}
            </div>
          )}

          {isLegsChange && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Contract Legs Change
              </span>
              <span className="text-xs text-muted-foreground">
                Update the steps for the implied pairing contract type. Clear a
                prefilled field to remove that leg. The steps you may edit are
                derived from the implied pairing type.
              </span>
              <div className="grid grid-cols-4 gap-3">
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Implied Pairing Type</Label>
                  <Combobox
                    dwimOnChange
                    isModal
                    value={legsChangeImpliedType}
                    onValueChange={(value: string) =>
                      setLegsChangeImpliedType(value)
                    }
                    options={ContractTypeOptions}
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {showLegsChangeStep("step_one") && (
                  <div className="grid items-center gap-1.5">
                    <Label className="font-semibold">Step 1</Label>
                    <Input
                      type="text"
                      value={typeChangeFields.step_one ?? ""}
                      onChange={(e) =>
                        setTypeChangeField("step_one", e.target.value || undefined)
                      }
                    />
                  </div>
                )}
                {showLegsChangeStep("step_two") && (
                  <div className="grid items-center gap-1.5">
                    <Label className="font-semibold">Step 2</Label>
                    <Input
                      type="text"
                      value={typeChangeFields.step_two ?? ""}
                      onChange={(e) =>
                        setTypeChangeField("step_two", e.target.value || undefined)
                      }
                    />
                  </div>
                )}
                {showLegsChangeStep("step_three") && (
                  <div className="grid items-center gap-1.5">
                    <Label className="font-semibold">Step 3</Label>
                    <Input
                      type="text"
                      value={typeChangeFields.step_three ?? ""}
                      onChange={(e) =>
                        setTypeChangeField("step_three", e.target.value || undefined)
                      }
                    />
                  </div>
                )}
                {showLegsChangeStep("double_up") && (
                  <div className="grid items-center gap-1.5">
                    <Label className="font-semibold">Double Up</Label>
                    <Input
                      type="text"
                      value={typeChangeFields.double_up ?? ""}
                      onChange={(e) =>
                        setTypeChangeField("double_up", e.target.value || undefined)
                      }
                    />
                  </div>
                )}
              </div>
              {legsChangeCanolaSteps.length > 0 && (
                <CanolaFuturesSection
                  steps={legsChangeCanolaSteps}
                  state={canola}
                  onChange={setCanolaField}
                  quantity={currentQuantity}
                />
              )}
            </div>
          )}

          {isSplit && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Split Fields
              </span>
              <span className="text-xs text-muted-foreground">
                Notes and Adjustment info set below will modify the existing contract. The new contract will inherit the existing latest premium and fee.
              </span>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Split Customer</Label>
                  <Combobox
                    isModal
                    dwimOnChange
                    virtualizeList
                    popoverClassName="min-w-[400px]"
                    value={splitCustomerId}
                    onValueChange={(value: string) => setSplitCustomerId(value)}
                    options={customerOptions}
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Split Quantity</Label>
                  <Input
                    type="text"
                    value={splitQuantity}
                    onChange={(e) => setSplitQuantity(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-6 text-sm">
                <span className="text-muted-foreground">
                  Original quantity: {currentQuantity ?? "n/a"}
                </span>
                {splitPreview && splitPreview.remaining !== null ? (
                  <>
                    <span>
                      Remaining: {splitPreview.remaining.toString()}
                    </span>
                    <span>New contract: {splitPreview.split.toString()}</span>
                  </>
                ) : (
                  splitQuantity.trim() !== "" && (
                    <span className="text-orange-500">
                      Split quantity must be greater than 0 and less than the
                      original quantity.
                    </span>
                  )
                )}
              </div>
              {isReown && (
                <div className="flex flex-col gap-2">
                  <ReownContractReferenceEditor
                    groups={splitRefGroups}
                    onChange={setSplitRefGroups}
                    disabled={false}
                    errors={splitRefErrors}
                  />
                </div>
              )}
            </div>
          )}

          {!isSupported && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Please select an amendment</AlertTitle>
              <AlertDescription>
              </AlertDescription>
            </Alert>
          )}

          {/* Shared adjustment (required on every amendment except expired) */}
          {!isExpired && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Adjustment (required)
              </span>
              <div className="grid grid-cols-3 gap-3">
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Adj Option Premium</Label>
                  <Input
                    type="text"
                    value={shared.adjustment.option_premium ?? ""}
                    onChange={(e) =>
                      setAdjustment("option_premium", e.target.value || undefined)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Adj Initial Premium + Fee</Label>
                  <Input
                    type="text"
                    value={shared.adjustment.contract_fee ?? ""}
                    onChange={(e) =>
                      setAdjustment("contract_fee", e.target.value || undefined)
                    }
                  />
                </div>
                <div className="grid items-center gap-1.5">
                  <Label className="font-semibold">Adjusted Date</Label>
                  <DatePicker
                    value={shared.adjustment.adjusted_date}
                    onChange={(date: Date | undefined) =>
                      setAdjustment("adjusted_date", date)
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {/* Shared common fields */}
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div className="grid items-center gap-1.5">
              <Label className="font-semibold">
                Amendment Trader{isExpired ? " (required)" : ""}
              </Label>
              <Combobox
                isModal
                dwimOnChange
                value={shared.amendment_trader_id ?? ""}
                onValueChange={(value: string) =>
                  setShared((prev: AmendmentSharedInput) => ({
                    ...prev,
                    amendment_trader_id: value || undefined,
                  }))
                }
                options={traderOptions}
                virtualizeList
                popoverClassName="w-[400px] h-[300px]"
              />
            </div>
            <div className="grid items-center gap-1.5">
              <Label className="font-semibold">
                Notes{isExpired ? " (required)" : ""}
              </Label>
              <Input
                type="text"
                value={shared.notes ?? ""}
                onChange={(e) =>
                  setShared((prev: AmendmentSharedInput) => ({
                    ...prev,
                    notes: e.target.value || undefined,
                  }))
                }
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || !isSupported}
          >
            {isSaving ? "Creating..." : "Create Amendment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
